import Ember from 'ember';
import { task } from 'ember-concurrency';
import CustomFieldDefinition, {
  buildCustomFieldFilter,
  CustomFieldDefinitionEvents,
  CustomFieldDefinitionResourceName
} from 'volta/models/custom-field-definition';
import {
  Events as PSEvents,
  ResourceName as PlanningSettingsResourceName
} from 'volta/models/planning-setting';
import { ResourceName as SkuResourceName, SkuEvents } from 'volta/models/sku';
import { SupplierEvents, SupplierResourceName } from 'volta/models/supplier';
import { ICollectionProperty } from 'volta/models/types/collection-view';
import { TImportTableName } from 'volta/models/types/data-import';
import { WorkshopEvents, WorkshopResourceName } from 'volta/models/workshop';
import EventStreamService from 'volta/services/event-stream';
import StoreService from 'volta/services/store';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter } from 'volta/utils/filters-utils';

import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

type TEntityType = 'inventories' | 'suppliers' | 'workshops' | string;
const EntityTypes: TEntityType[] = [
  SkuResourceName.table as string,
  SupplierResourceName.table as string,
  WorkshopResourceName.table as string
];

const customFieldsFilters = (definitions?: CustomFieldDefinition[], apiPrefix?: string) => {
  return (definitions ?? []).reduce((acc, def) => {
    const filter = buildCustomFieldFilter(def, apiPrefix);
    if (filter) {
      acc.push(filter);
    }
    return acc;
  }, [] as TFilterDefinition[]);
};

const customFieldsColumns = (definitions?: CustomFieldDefinition[], apiPrefix?: string) => {
  return (definitions ?? []).reduce((acc, def) => {
    if (!def) {
      return undefined;
    }

    const { name, slug, fieldType } = def;
    const propDef = {
      property: apiPrefix ? apiPrefix + '.customFields.' + slug : slug,
      width: 80,
      title: name,
      dataType: fieldType,
      isCustomField: true
    };
    if (propDef) {
      acc.push(propDef);
    }

    return acc;
  }, [] as ICollectionProperty[]);
};

export default class CustomFieldDefinitionsService extends Service {
  // Services
  @service store!: StoreService;
  @service eventStream!: EventStreamService;

  @tracked definitions: Partial<Record<TEntityType, CustomFieldDefinition[] | undefined>> = {};

  constructor() {
    super(...arguments);

    // Init definitions state
    EntityTypes.forEach((e: TEntityType) => (this.definitions[e] = undefined));

    // Setup websocket event handlers
    this.eventStream.addHandler(
      CustomFieldDefinitionResourceName.plural,
      [
        CustomFieldDefinitionEvents.CustomFieldDefinitionCreated,
        CustomFieldDefinitionEvents.CustomFieldDefinitionUpdated
      ],
      this.onUpserted,
      this
    );
    this.eventStream.addHandler(
      CustomFieldDefinitionResourceName.plural,
      [CustomFieldDefinitionEvents.CustomFieldDefinitionDeleted],
      this.onDeleted,
      this
    );
    this.eventStream.addHandler(
      SkuResourceName.plural,
      [SkuEvents.SkuCreated, SkuEvents.SkuUpdated, SkuEvents.ManySkuUpdated],
      this.onSkuUpdated,
      this
    );
    this.eventStream.addHandler(
      SupplierResourceName.plural,
      [SupplierEvents.SupplierCreated, SupplierEvents.SupplierUpdated],
      this.onSupplierUpdated,
      this
    );
    this.eventStream.addHandler(
      WorkshopResourceName.plural,
      [WorkshopEvents.WorkshopCreated, WorkshopEvents.WorkshopUpdated],
      this.onWorkshopUpdated,
      this
    );
    this.eventStream.addHandler(
      PlanningSettingsResourceName.plural,
      [PSEvents.ManyPlanningSettingsUpdated, PSEvents.PlanningSettingsUpdated],
      this.onSkuUpdated,
      this
    );
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  async fetchSkusDefinitions() {
    return await this.task.perform(SkuResourceName.table, false);
  }

  async fetchSuppliersDefinitions() {
    return await this.task.perform(SupplierResourceName.table, false);
  }

  async fetchWorkshopsDefinitions() {
    return await this.task.perform(WorkshopResourceName.table, false);
  }

  get skusDefinitions() {
    return SkuResourceName.table ? this.definitions[SkuResourceName.table] : [];
  }

  get suppliersDefinitions() {
    return SupplierResourceName.table ? this.definitions[SupplierResourceName.table] : [];
  }

  get workshopsDefinitions() {
    return WorkshopResourceName.table ? this.definitions[WorkshopResourceName.table] : [];
  }

  skusCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.skusDefinitions, apiPrefix);
  }

  skusCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.skusDefinitions, apiPrefix);
  }

  suppliersCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.suppliersDefinitions, apiPrefix);
  }

  workshopsCustomFieldsFilters(apiPrefix?: string) {
    return customFieldsFilters(this.workshopsDefinitions, apiPrefix);
  }

  workshopsCustomProperties(apiPrefix?: string) {
    return customFieldsColumns(this.workshopsDefinitions, apiPrefix);
  }

  addOne(def: CustomFieldDefinition) {
    const tableDefinition = this.definitions[def.tblName];
    const defExists = tableDefinition?.find((d: CustomFieldDefinition) => d.id === def.id);
    if (defExists) {
      return;
    }
    this.definitions[def.tblName] = tableDefinition ? [...tableDefinition, def] : [def];
  }

  removeOne(def: CustomFieldDefinition) {
    const tableDefinition = this.definitions[def.tblName];
    if (tableDefinition?.length) {
      this.definitions[def.tblName] = tableDefinition.filter(
        (d: CustomFieldDefinition) => d.id !== def.id
      );
    }
  }

  task = task(async (tblName: TImportTableName | undefined, force: boolean = false) => {
    try {
      if (!tblName || !EntityTypes.includes(`${tblName}`.toLowerCase())) {
        return;
      }

      if (this.definitions[tblName] && !force) {
        return this.definitions[tblName];
      }

      const query = {
        filter: {
          tblName: eqFilter(tblName)
        },
        page: { offset: 0, limit: 1000 },
        sort: 'order',
        include: tblName === SkuResourceName.table ? 'warehouses' : undefined
      };

      const definitions: Ember.Array<CustomFieldDefinition> = await this.store.query(
        CustomFieldDefinitionResourceName.singular,
        query
      );
      this.definitions = { ...this.definitions, [tblName]: definitions.toArray() };

      return definitions;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  onUpserted() {
    return;
  }

  onDeleted() {
    return;
  }

  onSkuUpdated() {
    return;
  }

  onSupplierUpdated() {
    return;
  }

  onWorkshopUpdated() {
    return;
  }
}

declare module '@ember/service' {
  interface Registry {
    'custom-field-definitions-service': CustomFieldDefinitionsService;
  }
}
